import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import {
	createResource,
	type RouterDataContext,
	type ResourceStoreContext,
} from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import type { ApplicationEditions } from '@atlassian/jira-shared-types/src/edition.tsx';
import type { KBTopicsResponse, FetchSuggestedTopicsResponse } from './types.tsx';

type AiEnablementResponse = {
	readonly isEnabled: boolean;
};
type ProjectResponse = {
	readonly id: string;
};

const isTopicSuggestionAllowed = (appEditions: ApplicationEditions) => {
	if (!fg('restrict-suggested-topics-to-jsm-premium')) {
		return true;
	}

	return appEditions.serviceDesk === 'PREMIUM_EDITION';
};

export const kbTopicsResource = createResource<KBTopicsResponse>({
	type: 'KB_TOPIC',
	getKey: ({ match }) => {
		return match.params.projectKey ? `kb-topic-${match.params.projectKey}` : 'kb-topic';
	},
	getData: async (
		{ match: { params } }: RouterDataContext,
		{ tenantContext: { activationId, appEditions } }: ResourceStoreContext,
	) => {
		if (!isTopicSuggestionAllowed(appEditions)) {
			return {
				topicSuggestionAllowed: false,
			};
		}
		// fetch project response and ai enablement response in parallel
		const [projectResponse, aiEnablementResponse] = await Promise.all([
			fetchJson<ProjectResponse>(`/rest/api/2/project/${params.projectKey}`),
			fetchJson<AiEnablementResponse>('/rest/api/1/aiconfig/ai-enabled/jira-servicedesk', {
				method: 'GET',
			}),
		]);
		// if ai enablement is enabled, fetch kb topics
		if (aiEnablementResponse.isEnabled) {
			const response = await fetchJson<FetchSuggestedTopicsResponse>(
				`/gateway/api/jsd-apollo-stargate/sharded/workspace/${activationId}/api/project/${projectResponse.id}/kb-topics`,
			);
			return {
				topics: response.topics,
				isFiltered: fg('remove-ui-topic-filtering') ? response.isFiltered : undefined,
				aiEnabled: true,
				topicSuggestionAllowed: true,
			};
		}
		return {
			topics: [],
			isFiltered: fg('remove-ui-topic-filtering') ? true : undefined,
			aiEnabled: false,
			topicSuggestionAllowed: true,
		};
	},
	isBrowserOnly: true,
	maxAge: 1000 * 30 * 60, // 30 minutes max age
});
